/**
 * Centralized state management for the Fyncall Widget
 * Provides a single source of truth for all widget state
 */

import { saveProactiveState, loadProactiveState } from '../utils/storage.js';

/**
 * Initial state structure
 */
const initialState = {
    // Session state
    session: null,
    visitorToken: null,
    hasConversation: false,
    sessionCheckComplete: false,

    // UI state
    isOpen: false,
    isSDKMode: false,
    unreadCount: 0,
    lastActivity: Date.now(),
    proactiveShown: false,

    // Cart state
    cartData: null,
    cartCount: 0,
    appliedPromoCode: null,
    appliedPromoDiscount: 0,

    // WebSocket state
    websocketConnected: false,
    reconnectAttempts: 0,

    // Shopify context
    shopifyContext: null,

    // CSAT survey state
    csatState: {
        shown: false,
        submitted: false,
        rating: null
    },

    // Message reactions by message ID
    messageReactions: {},

    // Proactive engagement state
    proactiveState: {
        triggersShown: {},
        triggerCount: 0,
        lastTriggerTime: 0,
        scrollDepth: 0,
        pageLoadTime: Date.now(),
        exitIntentBound: false,
        scrollListenerBound: false
    },

    // Current variant modal product/selection
    currentVariantProduct: null,
    selectedVariant: null,
    selectedOptions: {}
};

/**
 * Current state (copy of initial state)
 * @type {Object}
 */
let state = { ...initialState };

/**
 * State change subscribers
 * @type {Set<Function>}
 */
const subscribers = new Set();

/**
 * Get the entire state object (read-only reference)
 * @returns {Object} Current state
 */
export function getState() {
    return state;
}

/**
 * Get a specific state value by key path
 * @param {string} path - Dot-notation path (e.g., 'proactiveState.scrollDepth')
 * @returns {*} State value
 */
export function getStateValue(path) {
    return path.split('.').reduce((obj, key) => obj?.[key], state);
}

/**
 * Set a specific state value
 * @param {string} key - State key (top-level only)
 * @param {*} value - New value
 */
export function setState(key, value) {
    const oldValue = state[key];
    state[key] = value;
    notifySubscribers(key, value, oldValue);
}

/**
 * Update multiple state values at once
 * @param {Object} updates - Object with key-value pairs to update
 */
export function updateState(updates) {
    const changes = {};
    Object.keys(updates).forEach(key => {
        if (state[key] !== updates[key]) {
            changes[key] = { old: state[key], new: updates[key] };
            state[key] = updates[key];
        }
    });

    if (Object.keys(changes).length > 0) {
        Object.keys(changes).forEach(key => {
            notifySubscribers(key, changes[key].new, changes[key].old);
        });
    }
}

/**
 * Merge values into a nested state object
 * @param {string} key - Top-level state key
 * @param {Object} values - Values to merge
 */
export function mergeState(key, values) {
    const oldValue = state[key];
    state[key] = { ...state[key], ...values };
    notifySubscribers(key, state[key], oldValue);
}

/**
 * Subscribe to state changes
 * @param {Function} callback - Callback function (key, newValue, oldValue)
 * @returns {Function} Unsubscribe function
 */
export function subscribe(callback) {
    subscribers.add(callback);
    return () => subscribers.delete(callback);
}

/**
 * Notify all subscribers of a state change
 * @param {string} key - Changed key
 * @param {*} newValue - New value
 * @param {*} oldValue - Old value
 */
function notifySubscribers(key, newValue, oldValue) {
    subscribers.forEach(callback => {
        try {
            callback(key, newValue, oldValue);
        } catch (e) {
            console.error('[FyncallState] Subscriber error:', e);
        }
    });
}

/**
 * Reset state to initial values
 */
export function resetState() {
    state = { ...initialState, proactiveState: { ...initialState.proactiveState } };
    subscribers.forEach(callback => {
        try {
            callback('__reset__', state, null);
        } catch (e) {
            console.error('[FyncallState] Subscriber error during reset:', e);
        }
    });
}

// Convenience getters and setters for common state

/**
 * Get session ID
 * @returns {string|null} Session ID
 */
export function getSession() {
    return state.session;
}

/**
 * Set session ID
 * @param {string|null} sessionId - Session ID
 */
export function setSession(sessionId) {
    setState('session', sessionId);
}

/**
 * Get visitor token
 * @returns {string|null} Visitor token
 */
export function getVisitorToken() {
    return state.visitorToken;
}

/**
 * Set visitor token
 * @param {string|null} token - Visitor token
 */
export function setVisitorToken(token) {
    setState('visitorToken', token);
}

/**
 * Check if widget is open
 * @returns {boolean} Open state
 */
export function isWidgetOpen() {
    return state.isOpen;
}

/**
 * Set widget open state
 * @param {boolean} isOpen - Open state
 */
export function setWidgetOpen(isOpen) {
    setState('isOpen', isOpen);
}

/**
 * Check if in SDK mode
 * @returns {boolean} SDK mode state
 */
export function isInSDKMode() {
    return state.isSDKMode;
}

/**
 * Set SDK mode
 * @param {boolean} isSDK - SDK mode state
 */
export function setSDKMode(isSDK) {
    setState('isSDKMode', isSDK);
}

/**
 * Get unread count
 * @returns {number} Unread message count
 */
export function getUnreadCount() {
    return state.unreadCount;
}

/**
 * Set unread count
 * @param {number} count - Unread count
 */
export function setUnreadCount(count) {
    setState('unreadCount', count);
}

/**
 * Increment unread count
 */
export function incrementUnreadCount() {
    setState('unreadCount', state.unreadCount + 1);
}

/**
 * Clear unread count
 */
export function clearUnreadCount() {
    setState('unreadCount', 0);
}

/**
 * Get cart data
 * @returns {Object|null} Cart data
 */
export function getCartData() {
    return state.cartData;
}

/**
 * Set cart data
 * @param {Object|null} cartData - Cart data
 */
export function setCartData(cartData) {
    setState('cartData', cartData);
    if (cartData?.items) {
        setState('cartCount', cartData.items.reduce((sum, item) => sum + (item.quantity || 1), 0));
    } else {
        setState('cartCount', 0);
    }
}

/**
 * Get cart count
 * @returns {number} Cart item count
 */
export function getCartCount() {
    return state.cartCount;
}

/**
 * Get proactive state
 * @returns {Object} Proactive engagement state
 */
export function getProactiveState() {
    return state.proactiveState;
}

/**
 * Update proactive state
 * @param {Object} updates - Values to merge into proactive state
 */
export function updateProactiveState(updates) {
    mergeState('proactiveState', updates);
    // Persist to session storage
    saveProactiveState(state.proactiveState);
}

/**
 * Initialize proactive state from session storage
 */
export function initProactiveState() {
    const saved = loadProactiveState();
    if (saved) {
        mergeState('proactiveState', saved);
    }
}

/**
 * Mark a proactive trigger as shown
 * @param {string} triggerId - Trigger ID
 */
export function markTriggerShown(triggerId) {
    const proactive = state.proactiveState;
    updateProactiveState({
        triggersShown: { ...proactive.triggersShown, [triggerId]: true },
        triggerCount: proactive.triggerCount + 1,
        lastTriggerTime: Date.now()
    });
}

/**
 * Check if a trigger has been shown
 * @param {string} triggerId - Trigger ID
 * @returns {boolean} Whether trigger was shown
 */
export function hasTriggerBeenShown(triggerId) {
    return !!state.proactiveState.triggersShown[triggerId];
}

/**
 * Get Shopify context
 * @returns {Object|null} Shopify context
 */
export function getShopifyContext() {
    return state.shopifyContext;
}

/**
 * Set Shopify context
 * @param {Object|null} context - Shopify context
 */
export function setShopifyContext(context) {
    setState('shopifyContext', context);
}

/**
 * Get message reactions
 * @param {string} messageId - Message ID
 * @returns {string|null} Reaction emoji
 */
export function getMessageReaction(messageId) {
    return state.messageReactions[messageId] || null;
}

/**
 * Set message reaction
 * @param {string} messageId - Message ID
 * @param {string|null} reaction - Reaction emoji or null to remove
 */
export function setMessageReaction(messageId, reaction) {
    const reactions = { ...state.messageReactions };
    if (reaction) {
        reactions[messageId] = reaction;
    } else {
        delete reactions[messageId];
    }
    setState('messageReactions', reactions);
}

/**
 * Check if there's an active conversation
 * @returns {boolean} Has conversation
 */
export function hasActiveConversation() {
    return state.hasConversation;
}

/**
 * Set conversation active state
 * @param {boolean} hasConversation - Has conversation
 */
export function setHasConversation(hasConversation) {
    setState('hasConversation', hasConversation);
}

/**
 * Update last activity timestamp
 */
export function updateLastActivity() {
    setState('lastActivity', Date.now());
}

/**
 * Get last activity timestamp
 * @returns {number} Timestamp
 */
export function getLastActivity() {
    return state.lastActivity;
}
